<template>
  <modal
    name="LayerOrderAdminModal"
    :width="'90%'"
    :max-width="680"
    height="auto"
    :adaptive="true"
    :scrollable="true"
    class="layer-order-vmodal"
    @closed="resetState"
  >
    <section class="layer-order-modal" aria-labelledby="layer-order-title">
      <header class="layer-order-header">
        <div>
          <h2 id="layer-order-title">Definir orden de capas</h2>
          <p>Seleccione una categoría y ordene solamente las capas que pertenecen a ella.</p>
        </div>
        <button type="button" class="close-button" title="Cerrar" :disabled="saving" @click="close">&times;</button>
      </header>

      <div class="layer-order-body">
        <label class="category-field">
          Categoría
          <select v-model="selectedCategory" :disabled="loadingCategories || saving" @change="loadLayers">
            <option value="" disabled>Seleccione una categoría</option>
            <option v-for="category in orderCategories" :key="category.id" :value="category.id">
              {{ category.name }}
            </option>
          </select>
        </label>

        <div v-if="loadingCategories || loadingLayers" class="layer-order-state">
          <font-awesome-icon icon="spinner" spin />
          <span>Cargando capas...</span>
        </div>
        <div v-else-if="errorMessage && orderedLayers.length === 0" class="layer-order-error" role="alert">
          {{ errorMessage }}
        </div>
        <div v-else-if="orderCategories.length === 0" class="layer-order-state">
          No hay categorías disponibles.
        </div>
        <div v-else-if="selectedCategory && orderedLayers.length === 0" class="layer-order-state">
          Esta categoría no tiene capas para ordenar.
        </div>

        <ol v-else-if="orderedLayers.length" class="layer-order-list">
          <li
            v-for="(layer, index) in orderedLayers"
            :key="layer.id"
            :class="{ dragging: draggedIndex === index }"
            draggable="true"
            @dragstart="startDrag(index, $event)"
            @dragover.prevent
            @drop="dropLayer(index)"
            @dragend="draggedIndex = null"
          >
            <span class="layer-position">{{ index + 1 }}</span>
            <span class="drag-handle" title="Arrastrar para reordenar"><font-awesome-icon icon="sort" /></span>
            <span class="layer-name">{{ layer.name }}</span>
            <div class="layer-order-controls">
              <button type="button" title="Subir capa" :disabled="index === 0 || saving" @click="moveLayer(index, -1)">
                <font-awesome-icon icon="arrow-up" />
              </button>
              <button type="button" title="Bajar capa" :disabled="index === orderedLayers.length - 1 || saving" @click="moveLayer(index, 1)">
                <font-awesome-icon icon="arrow-down" />
              </button>
            </div>
          </li>
        </ol>

        <div v-if="errorMessage && orderedLayers.length" class="layer-order-error" role="alert">{{ errorMessage }}</div>
      </div>

      <footer class="layer-order-footer">
        <button type="button" class="button-secondary" :disabled="saving" @click="close">Cancelar</button>
        <button
          type="button"
          class="button-primary"
          :disabled="loadingCategories || loadingLayers || saving || !selectedCategory || !orderedLayers.length || !hasChanges"
          @click="saveOrder"
        >
          <font-awesome-icon v-if="saving" icon="spinner" spin />
          {{ saving ? 'Guardando...' : 'Guardar orden' }}
        </button>
      </footer>
    </section>
  </modal>
</template>

<script>
import { restCategory } from '@/components/mixin/restCategory.js'
import { restLayer } from '@/components/mixin/restLayer.js'

export default {
  name: 'LayerOrderAdminModal',
  mixins: [restCategory, restLayer],
  props: {
    initialCategory: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      orderCategories: [],
      selectedCategory: '',
      orderedLayers: [],
      originalOrder: [],
      loadingCategories: false,
      loadingLayers: false,
      saving: false,
      errorMessage: '',
      draggedIndex: null,
      requestSequence: 0
    }
  },
  computed: {
    hasChanges() {
      const orderChanged = this.orderedLayers.some((layer, index) => layer.id !== this.originalOrder[index])
      const needsNormalization = this.orderedLayers.some((layer, index) => Number(layer.orden || 0) !== index + 1)
      return orderChanged || needsNormalization
    }
  },
  methods: {
    show() {
      this.resetState()
      this.loadingCategories = true
      this.$modal.show('LayerOrderAdminModal')
      this.getCategoriesForOrder()
        .then(categories => {
          this.orderCategories = categories
          if (categories.length) {
            const preferredCategory = categories.find(category => category.id === this.initialCategory)
            this.selectedCategory = preferredCategory ? preferredCategory.id : categories[0].id
            this.loadLayers()
          }
        })
        .catch(error => {
          this.errorMessage = this.getErrorMessage(error, 'No fue posible cargar las categorías.')
        })
        .then(() => {
          this.loadingCategories = false
        })
    },
    loadLayers() {
      const requestID = ++this.requestSequence
      this.orderedLayers = []
      this.originalOrder = []
      this.errorMessage = ''
      if (!this.selectedCategory) return
      this.loadingLayers = true
      this.getLayersForOrder(this.selectedCategory)
        .then(layers => {
          if (requestID !== this.requestSequence) return
          this.orderedLayers = layers.map(layer => ({ ...layer }))
          this.originalOrder = layers.map(layer => layer.id)
        })
        .catch(error => {
          if (requestID !== this.requestSequence) return
          this.errorMessage = this.getErrorMessage(error, 'No fue posible cargar las capas de la categoría.')
        })
        .then(() => {
          if (requestID === this.requestSequence) this.loadingLayers = false
        })
    },
    close() {
      if (!this.saving) this.$modal.hide('LayerOrderAdminModal')
    },
    resetState() {
      this.requestSequence++
      this.orderCategories = []
      this.selectedCategory = ''
      this.orderedLayers = []
      this.originalOrder = []
      this.loadingCategories = false
      this.loadingLayers = false
      this.saving = false
      this.errorMessage = ''
      this.draggedIndex = null
    },
    startDrag(index, event) {
      if (this.saving) return
      this.draggedIndex = index
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', String(index))
    },
    dropLayer(targetIndex) {
      if (this.draggedIndex === null || this.draggedIndex === targetIndex || this.saving) return
      const movedLayer = this.orderedLayers.splice(this.draggedIndex, 1)[0]
      this.orderedLayers.splice(targetIndex, 0, movedLayer)
      this.draggedIndex = null
    },
    moveLayer(index, direction) {
      const targetIndex = index + direction
      if (targetIndex < 0 || targetIndex >= this.orderedLayers.length) return
      const movedLayer = this.orderedLayers.splice(index, 1)[0]
      this.orderedLayers.splice(targetIndex, 0, movedLayer)
    },
    saveOrder() {
      if (!this.hasChanges || this.saving) return
      this.saving = true
      this.errorMessage = ''
      const layerIDs = this.orderedLayers.map(layer => layer.id)
      this.putLayersOrder(this.selectedCategory, layerIDs)
        .then(() => {
          this.$emit('saved')
          this.$modal.hide('LayerOrderAdminModal')
        })
        .catch(error => {
          this.errorMessage = this.getErrorMessage(error, 'No fue posible guardar el orden de las capas.')
        })
        .then(() => {
          this.saving = false
        })
    },
    getErrorMessage(error, fallback) {
      return error && error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : fallback
    }
  }
}
</script>

<style lang="scss" scoped>
.layer-order-vmodal::v-deep .vm--modal { background: transparent; box-shadow: none; }
.layer-order-modal { overflow: hidden; width: 100%; max-height: 88vh; border-radius: 8px; background: #fff; box-shadow: 0 18px 48px rgba(15,42,61,.28); color: var(--color-navy); }
.layer-order-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; padding: 1.25rem 1.4rem; border-bottom: 1px solid #dfe6eb; }
.layer-order-header h2 { margin: 0 0 .35rem; font-size: 1.15rem; }
.layer-order-header p { margin: 0; color: #667786; font-size: .82rem; line-height: 1.45; }
.close-button { flex: 0 0 auto; width: 30px; height: 30px; border: 0; border-radius: 4px; background: transparent; color: #667786; font-size: 1.4rem; cursor: pointer; }
.close-button:hover:not(:disabled) { background: #edf2f5; color: var(--color-navy); }
.layer-order-body { overflow-y: auto; max-height: 60vh; padding: 1rem 1.4rem; background: #f7f9fa; }
.category-field { display: flex; flex-direction: column; gap: .35rem; margin-bottom: .9rem; color: #32485a; font-size: .82rem; font-weight: 600; }
.category-field select { width: 100%; padding: .45rem .55rem; border: 1px solid #cbd6de; border-radius: 4px; background: #fff; }
.layer-order-state { display: flex; align-items: center; justify-content: center; gap: .6rem; min-height: 120px; color: #667786; }
.layer-order-list { display: flex; flex-direction: column; gap: .45rem; margin: 0; padding: 0; list-style: none; }
.layer-order-list li { display: grid; grid-template-columns: 32px 24px minmax(0,1fr) auto; align-items: center; gap: .55rem; min-height: 44px; padding: .45rem .55rem; border: 1px solid #d7e0e6; border-radius: 5px; background: #fff; cursor: grab; }
.layer-order-list li.dragging { opacity: .5; border-color: var(--color-orange); }
.layer-position { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 4px; background: #edf2f5; font-size: .78rem; font-weight: 700; }
.drag-handle { color: #8a9aa7; text-align: center; }
.layer-name { overflow: hidden; color: #32485a; font-size: .86rem; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.layer-order-controls { display: flex; gap: .35rem; }
.layer-order-controls button { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border: 1px solid #cbd6de; border-radius: 4px; background: #fff; color: var(--color-navy); cursor: pointer; }
.layer-order-controls button:hover:not(:disabled) { border-color: var(--color-orange); color: var(--color-orange); }
.layer-order-controls button:disabled,.close-button:disabled { opacity: .4; cursor: not-allowed; }
.layer-order-error { margin-top: .8rem; padding: .65rem .8rem; border: 1px solid #edb9b5; border-radius: 4px; background: #fff1f0; color: #922b21; font-size: .82rem; }
.layer-order-footer { display: flex; justify-content: flex-end; gap: .6rem; padding: 1rem 1.4rem; border-top: 1px solid #dfe6eb; background: #fff; }
.layer-order-footer button { display: inline-flex; align-items: center; justify-content: center; gap: .45rem; min-height: 36px; padding: .45rem .9rem; border-radius: 4px; font-size: .82rem; font-weight: 600; cursor: pointer; }
.button-secondary { border: 1px solid #cbd6de; background: #fff; color: var(--color-navy); }
.button-primary { border: 1px solid var(--color-navy); background: var(--color-navy); color: #fff; }
.layer-order-footer button:disabled { opacity: .5; cursor: not-allowed; }
@media (max-width: 520px) { .layer-order-list li { grid-template-columns: 28px 20px minmax(0,1fr); } .layer-order-controls { grid-column: 1 / -1; justify-content: flex-end; } }
</style>
